import { useEffect, useRef, useCallback, useState } from 'react';
import { useAuth } from './useAuth';
import { toast } from 'sonner';

const DEFAULT_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const EXTENDED_TIMEOUT = 7 * 24 * 60 * 60 * 1000; // 7 days
const WARNING_DURATION = 5 * 60 * 1000; // 5 minutes before timeout

const getTimeoutDuration = () => {
  const rememberMe = localStorage.getItem('rememberMe') === 'true';
  return rememberMe ? EXTENDED_TIMEOUT : DEFAULT_TIMEOUT;
};

export const useSessionTimeout = () => {
  const { user, signOut } = useAuth();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningRef = useRef<NodeJS.Timeout | null>(null);
  const [showWarning, setShowWarning] = useState(false);

  const handleLogout = useCallback(async () => {
    setShowWarning(false);
    toast.info('Session expired due to inactivity');
    await signOut();
  }, [signOut]);

  const resetTimer = useCallback(() => {
    if (!user) return;

    setShowWarning(false);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);

    const timeoutDuration = getTimeoutDuration();

    // Only show warning for non-extended sessions
    if (timeoutDuration === DEFAULT_TIMEOUT) {
      warningRef.current = setTimeout(() => {
        setShowWarning(true);
        toast.warning('Session will expire in 5 minutes due to inactivity', {
          duration: 10000,
        });
      }, timeoutDuration - WARNING_DURATION);
    }

    // Actual logout
    timeoutRef.current = setTimeout(handleLogout, timeoutDuration);
  }, [user, handleLogout]);

  const extendSession = useCallback(() => {
    resetTimer();
    if (showWarning) {
      toast.success('Session extended');
    }
  }, [resetTimer, showWarning]);

  useEffect(() => {
    if (!user) return;

    const events = ['mousedown', 'keydown', 'mousemove', 'touchstart', 'scroll'];
    
    const handleActivity = () => resetTimer();

    events.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    resetTimer();

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
    };
  }, [user, resetTimer]);

  return { showWarning, extendSession };
};
